import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import OptimizedImage from './OptimizedImage';
import '../styles/markdown.css';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Extract front matter if present (remove the YAML front matter from the content)
  const processedContent = content.replace(/^---[\s\S]*?---/, '').trim();
  
  // Log the content for debugging
  console.log(`Rendering markdown content, length: ${processedContent.length} characters`);
  if (processedContent.length < 50) {
    console.log('Content might be too short or empty:', processedContent);
  }

  // Process ASCII charts and tables
  useEffect(() => {
    // Find pre elements that contain ASCII charts
    const preElements = document.querySelectorAll('.markdown-content pre');
    
    preElements.forEach(pre => {
      const codeElement = pre.querySelector('code');
      if (!codeElement) return;
      
      const content = codeElement.textContent || '';
      
      // Check if this is likely an ASCII chart or table
      const isAsciiChart = (
        (content.includes('┌') || content.includes('└') || content.includes('│') || 
         content.includes('─') || content.includes('┼') || content.includes('┬')) ||
        (content.includes('+') && content.includes('-') && content.includes('|') && 
         content.includes('\n') && !content.includes('function') && !content.includes('var '))
      );
      
      // Check if this is likely a graph with percentage signs and bars
      const isGraph = content.includes('%') && 
                     (content.includes('┼') || content.includes('-')) && 
                     (content.includes('╭') || content.includes('╰') || 
                      content.includes('/') || content.includes('\\'));
      
      if (isAsciiChart) {
        pre.classList.add('ascii-chart');
      }
      
      if (isGraph) {
        pre.classList.add('chart');
      }
    });
  }, [processedContent]);

  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={atomDark}
                language={match[1]}
                PreTag="div"
                className="rounded-md my-4 text-sm"
                showLineNumbers={true}
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={`bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 ${className}`} {...props}>
                {children}
              </code>
            );
          },
          img({ src, alt, ...props }: any) {
            return (
              <div className="my-6">
                <OptimizedImage 
                  src={src} 
                  alt={alt || ''} 
                  className="rounded-lg w-full h-auto shadow-lg" 
                  {...props} 
                />
                {alt && <p className="text-center text-sm text-gray-500 mt-2">{alt}</p>}
              </div>
            );
          },
          a({ href, children, ...props }: any) {
            return (
              <a 
                href={href} 
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors" 
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                {...props}
              >
                {children}
              </a>
            );
          },
          table({ children, ...props }: any) {
            return (
              <div className="overflow-x-auto my-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700" {...props}>
                  {children}
                </table>
              </div>
            );
          },
          th({ children, ...props }: any) {
            return (
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800" {...props}>
                {children}
              </th>
            );
          },
          td({ children, ...props }: any) {
            return (
              <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700" {...props}>
                {children}
              </td>
            );
          },
          pre({ children, ...props }: any) {
            // Special handling for pre elements that might contain ASCII art
            return (
              <pre {...props}>
                {children}
              </pre>
            );
          },
          p({ children, ...props }: any) {
            return (
              <p className="my-4 text-gray-800 dark:text-gray-300" {...props}>
                {children}
              </p>
            );
          },
          h1({ children, ...props }: any) {
            return (
              <h1 className="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-white" {...props}>
                {children}
              </h1>
            );
          },
          h2({ children, ...props }: any) {
            return (
              <h2 className="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-white" {...props}>
                {children}
              </h2>
            );
          },
          h3({ children, ...props }: any) {
            return (
              <h3 className="text-xl font-bold mt-5 mb-2 text-gray-900 dark:text-white" {...props}>
                {children}
              </h3>
            );
          },
          ul({ children, ...props }: any) {
            return (
              <ul className="list-disc pl-6 my-4 text-gray-800 dark:text-gray-300" {...props}>
                {children}
              </ul>
            );
          },
          ol({ children, ...props }: any) {
            return (
              <ol className="list-decimal pl-6 my-4 text-gray-800 dark:text-gray-300" {...props}>
                {children}
              </ol>
            );
          },
          blockquote({ children, ...props }: any) {
            return (
              <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-gray-50 dark:bg-gray-800 italic" {...props}>
                {children}
              </blockquote>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
